import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../hooks/useApi.js';
import { guidesForPath } from '../../help/guides.js';

// ── Route → title map ─────────────────────────────────────────────────────────
const TITLES = {
  '/':           'Dashboard',
  '/production': 'Production',
  '/stock':      'Stock',
  '/clients':    'Contract Clients',
  '/own-brand':  'Own Brand',
  '/outlets':    'Outlets',
  '/hmrc':       'HMRC',
  '/salsa':      'SALSA Compliance',
  '/reports':    'Reports',
  '/settings':   'Settings',
  '/help':       'Help',
};

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

// ── Notification drawer ───────────────────────────────────────────────────────
// Clicking a notification marks it read and opens what it's about; ✕ clears
// it; "Mark all as read" clears the lot. The badge follows (Joe, 29 Oct 2026).
function NotificationDrawer({ open, onClose, onChanged }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const drop = (id) => { setNotifications((ns) => ns.filter((x) => x.notification_id !== id)); onChanged?.(); };
  async function openOne(n) {
    try { await apiFetch('/notifications/read', { method: 'POST', body: { notificationIds: [n.notification_id] } }); } catch { /* still go */ }
    drop(n.notification_id);
    if (n.action_url) { onClose(); navigate(n.action_url); }
  }
  async function clearOne(e, n) {
    e.stopPropagation();
    try { await apiFetch(`/notifications/${n.notification_id}`, { method: 'DELETE' }); drop(n.notification_id); } catch { /* leave it */ }
  }
  async function readAll() {
    try { await apiFetch('/notifications/read-all', { method: 'POST', body: {} }); setNotifications([]); onChanged?.(); } catch { /* leave them */ }
  }

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiFetch('/notifications/unread')
      .then(r => setNotifications(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className="drawer">
        <div className="drawer-header">
          <span>Notifications</span>
          <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {notifications.length > 0 && <button type="button" className="drawer-readall" onClick={readAll}>Mark all as read</button>}
            <button className="drawer-close" onClick={onClose} aria-label="Close">✕</button>
          </span>
        </div>
        <div className="drawer-body">
          {loading && <p className="drawer-empty">Loading…</p>}
          {!loading && notifications.length === 0 && (
            <p className="drawer-empty">No unread notifications</p>
          )}
          {notifications.map(n => (
            <div key={n.notification_id} className={`drawer-item priority-${n.priority}`} role="button" tabIndex={0}
              onClick={() => openOne(n)} onKeyDown={(e) => { if (e.key === 'Enter') openOne(n); }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div className="drawer-item-title" style={{ flex: 1 }}>{n.title}</div>
                <button type="button" className="drawer-x" onClick={(e) => clearOne(e, n)} aria-label="Clear this notification">✕</button>
              </div>
              <div className="drawer-item-msg">{n.message}</div>
              <div className="drawer-item-time">{new Date(n.created_at).toLocaleString('en-GB')}{n.action_url ? ' · open' : ''}</div>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        .drawer-overlay {
          position: fixed; inset: 0; z-index: 299;
          background: rgba(0,0,0,.2);
        }
        .drawer {
          position: fixed; top: 0; right: 0; bottom: 0; width: 360px;
          background: var(--surface); z-index: 300;
          box-shadow: var(--shadow-lg);
          display: flex; flex-direction: column;
        }
        .drawer-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border);
          font-weight: 600;
          min-height: var(--header-height);
        }
        .drawer-close { color: var(--text-muted); font-size: 16px; }
        .drawer-close:hover { color: var(--text); }
        .drawer-body { flex: 1; overflow-y: auto; }
        .drawer-empty { padding: 24px; color: var(--text-muted); text-align: center; }
        .drawer-item {
          padding: 14px 20px;
          border-bottom: 1px solid var(--border);
          border-left: 3px solid transparent;
        }
        .priority-critical { border-left-color: var(--status-critical); }
        .priority-high     { border-left-color: var(--status-major); }
        .priority-medium   { border-left-color: var(--pld-green); }
        .drawer-item-title { font-weight: 600; font-size: 13px; margin-bottom: 3px; }
        .drawer-item-msg   { color: var(--text-muted); font-size: 12px; line-height: 1.5; white-space: pre-line; }
        .drawer-item { cursor: pointer; }
        .drawer-item:hover { background: var(--surface-2); }
        .drawer-x { background: none; border: none; color: var(--text-muted); cursor: pointer; font-size: 13px; padding: 0 2px; }
        .drawer-x:hover { color: var(--text); }
        .drawer-readall { background: none; border: none; color: var(--pld-green); font-weight: 600; font-size: 12px; cursor: pointer; }
        .drawer-item-time  { color: var(--text-light); font-size: 11px; margin-top: 4px; }
      `}</style>
    </>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────
export default function Header({ onToggleSidebar, onOpenAI }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pollRef = useRef(null);

  const handleLogout = async () => {
    try {
      await apiFetch('/auth/logout-all', { method: 'POST' });
    } catch {
      // Even if the server call fails, still clear local state and redirect.
    } finally {
      localStorage.removeItem('pld_token');
      navigate('/login', { replace: true });
    }
  };

  const title = Object.entries(TITLES).find(([path]) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)
  )?.[1] || 'Blake';

  // Poll for unread count every 30s (and straight after reading one)
  const refreshCount = () => apiFetch('/notifications/unread-count').then(r => setUnreadCount(r.data?.count || 0)).catch(() => {});
  useEffect(() => {
    const poll = refreshCount;
    poll();
    pollRef.current = setInterval(poll, 30000);
    return () => clearInterval(pollRef.current);
  }, []);

  // ⌘K → open AI
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenAI?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpenAI]);

  return (
    <>
      <header className="header">
        {/* Left */}
        <div className="header-left">
          <button className="header-menu-btn" onClick={onToggleSidebar} aria-label="Toggle sidebar">
            <MenuIcon />
          </button>
          <h1 className="header-title">{title}</h1>
        </div>

        {/* Right */}
        <div className="header-right">
          {/* AI shortcut (desktop) */}
          <button className="header-ai-btn" onClick={onOpenAI} title="Open AI assistant (⌘K)">
            <span className="header-ai-icon">✦</span>
            <span className="header-ai-label">Ask AI</span>
            <kbd className="header-ai-kbd">⌘K</kbd>
          </button>

          {/* Help for this page (Joe, 4 Nov 2026) */}
          <button
            className="header-icon-btn header-help-btn"
            onClick={() => navigate(['/', '/help'].includes(location.pathname) || !guidesForPath(location.pathname).length ? '/help' : `/help?page=${encodeURIComponent(location.pathname)}`)}
            aria-label="Help for this page"
            title="Help for this page"
          >?</button>

          {/* Notification bell */}
          <button
            className="header-icon-btn"
            onClick={() => setDrawerOpen(true)}
            aria-label="Notifications"
          >
            <BellIcon />
            {unreadCount > 0 && (
              <span className="header-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>

          {/* Logout */}
          <button
            className="header-icon-btn"
            onClick={handleLogout}
            aria-label="Log out"
            title="Log out"
          >
            <LogoutIcon />
          </button>
        </div>
      </header>

      <NotificationDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onChanged={refreshCount} />

      <style>{`
        .header {
          position: fixed;
          top: 0; left: var(--sidebar-width); right: 0;
          height: var(--header-height);
          background: var(--surface);
          border-bottom: 1px solid var(--border);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          z-index: 99;
          transition: left var(--transition);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .header-menu-btn {
          color: var(--text-muted);
          padding: 6px;
          border-radius: var(--radius-sm);
          transition: background var(--transition), color var(--transition);
        }
        .header-menu-btn:hover { background: var(--surface-2); color: var(--text); }

        .header-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--text);
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .header-ai-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: var(--radius);
          border: 1px solid var(--border);
          background: var(--surface-2);
          color: var(--text-muted);
          font-size: 13px;
          transition: all var(--transition);
        }
        .header-ai-btn:hover {
          border-color: var(--pld-green);
          color: var(--pld-green);
          background: rgba(34,57,31,.04);
        }

        .header-ai-icon { font-size: 14px; color: var(--pld-green); }
        .header-ai-label { font-weight: 500; }
        .header-ai-kbd {
          font-family: inherit;
          font-size: 11px;
          padding: 1px 5px;
          border: 1px solid var(--border);
          border-radius: 3px;
          background: var(--surface);
          color: var(--text-light);
        }

        .header-icon-btn {
          position: relative;
          padding: 7px;
          border-radius: var(--radius-sm);
          color: var(--text-muted);
          transition: background var(--transition), color var(--transition);
        }
        .header-icon-btn:hover { background: var(--surface-2); color: var(--text); }

        .header-help-btn {
          width: 34px; height: 34px; padding: 0;
          font-size: 16px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
        }

        .header-badge {
          position: absolute;
          top: 2px; right: 2px;
          min-width: 16px;
          height: 16px;
          background: var(--status-critical);
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          border-radius: 99px;
          padding: 0 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (max-width: 768px) {
          .header { left: 56px; }
          .header-ai-label, .header-ai-kbd { display: none; }
        }
      `}</style>
    </>
  );
}
